import React from "react";
import { Box, Container, Grid, Typography, Chip } from "@mui/material";
import { StoreThemeTokens } from "./themes/storeThemes";
import {
  CategoryItem,
  getStoreCategories,
  renderCategoryIcon,
} from "../../utils/categoryHelper";

interface CategoryGridProps {
  onSelectCategory?: (category: string) => void;
  selectedCategory?: string;
  themeTokens: StoreThemeTokens;
  categories?: CategoryItem[];
  storeData?: any;
}

const CategoryGrid: React.FC<CategoryGridProps> = ({
  onSelectCategory,
  selectedCategory,
  themeTokens,
  categories: propCategories,
  storeData,
}) => {
  const categoriesList = propCategories || getStoreCategories(storeData);
  const enabledCategories = categoriesList.filter((c) => c.enabled !== false);

  const showSection =
    storeData?.show_category_section !== undefined
      ? Boolean(storeData.show_category_section)
      : storeData?.header?.show_category_section !== undefined
      ? Boolean(storeData.header.show_category_section)
      : true;

  if (!showSection || enabledCategories.length === 0) {
    return null;
  }

  const handleCardClick = (catName: string) => {
    if (onSelectCategory) {
      onSelectCategory(catName);
    }
    const section = document.getElementById("products-section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const mdCols =
    enabledCategories.length <= 2
      ? 6
      : enabledCategories.length === 3
      ? 4
      : enabledCategories.length === 4
      ? 3
      : 3;

  return (
    <Box
      component="section"
      id="categories"
      sx={{
        py: { xs: 5, md: 7 },
        bgcolor: themeTokens.bgSecondary,
        borderBottom: `1px solid ${themeTokens.cardBorder}`,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Heading */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "flex-end" },
            justifyContent: "space-between",
            mb: 4,
            gap: 1,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.15em",
                color: themeTokens.accent,
                display: "block",
                fontSize: "0.75rem",
              }}
            >
              Curated Catalog
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: themeTokens.fontSerif,
                fontWeight: 600,
                color: themeTokens.textPrimary,
                fontSize: { xs: "1.75rem", sm: "2.1rem" },
                mt: 0.5,
              }}
            >
              Shop by Category
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: themeTokens.textSecondary,
              fontWeight: 500,
              fontSize: "0.8rem",
            }}
          >
            Handcrafted with premium finish &amp; warranty
          </Typography>
        </Box>

        {/* Dynamic Categories Grid */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {enabledCategories.map((cat) => {
            const isSelected = selectedCategory === cat.name;
            const isOffer =
              Boolean(cat.isOffer) ||
              cat.tag?.toUpperCase() === "SALE" ||
              cat.name.toLowerCase().includes("special offer") ||
              cat.name.toLowerCase().includes("sale");
            const badgeText = cat.tag || (isOffer ? "SALE" : "");

            return (
              <Grid item xs={6} sm={6} md={mdCols} key={cat.id || cat.name}>
                <Box
                  onClick={() => handleCardClick(cat.name)}
                  sx={{
                    bgcolor: themeTokens.cardBg,
                    borderRadius: 4,
                    p: { xs: 2, sm: 2.75 },
                    textAlign: "center",
                    position: "relative",
                    border: `1px solid ${isSelected ? themeTokens.accent : themeTokens.cardBorder}`,
                    boxShadow: isSelected
                      ? `0 8px 24px -4px ${themeTokens.accent}33`
                      : "0 4px 16px -2px rgba(0,0,0,0.04)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    transform: isSelected ? "translateY(-4px)" : "none",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      borderColor: isOffer ? "#f43f5e" : themeTokens.accent,
                      boxShadow: "0 12px 28px -4px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* Badge pill in top right corner if tag exists */}
                  {badgeText && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                      }}
                    >
                      <Chip
                        label={badgeText}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          bgcolor: isOffer ? "#ffe4e6" : "#f1f5f9",
                          color: isOffer ? "#e11d48" : "#475569",
                          textTransform: "uppercase",
                        }}
                      />
                    </Box>
                  )}

                  {/* Icon Box */}
                  <Box
                    sx={{
                      height: { xs: 80, sm: 100 },
                      borderRadius: 3,
                      bgcolor: isOffer ? "#fff1f2" : themeTokens.bgSecondary,
                      color: isOffer ? "#e11d48" : themeTokens.accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    {renderCategoryIcon(cat.icon, {
                      fontSize: 36,
                      color: isOffer ? "#e11d48" : themeTokens.accent,
                    })}
                  </Box>

                  {/* Name */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: isOffer ? "#e11d48" : themeTokens.textPrimary,
                      fontSize: { xs: "0.88rem", sm: "0.98rem" },
                      lineHeight: 1.25,
                    }}
                  >
                    {cat.name}
                  </Typography>

                  {/* Subtitle */}
                  {cat.subtitle && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isOffer ? "#f43f5e" : themeTokens.textSecondary,
                        fontSize: { xs: "0.72rem", sm: "0.78rem" },
                        mt: 0.5,
                        display: "block",
                      }}
                    >
                      {cat.subtitle}
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default CategoryGrid;
